import type { DateTime } from 'luxon'
import useUserPreferencesStore from '@/store/UserPreferencesStore'

function capitalizeFirst(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * Get relative date label with i18n support
 * @param target - Target DateTime
 * @param base - Base DateTime (usually now)
 * @param t - Optional translation function for special labels
 */
export function getRelativeDueDateLabel(
  target: DateTime,
  base: DateTime,
  t?: (key: string) => string,
) {
  const targetStart = target.startOf('day')
  const baseStart = base.startOf('day')
  const diffDays = Math.round(targetStart.diff(baseStart, 'days').days)

  // Use translation function if provided
  if (t) {
    if (diffDays === 0) {
      return t('time.today')
    }

    if (diffDays === 1) {
      return t('time.tomorrow')
    }

    if (diffDays === -1) {
      return t('time.yesterday')
    }
  } else {
    // Fallback to English if no translation function
    if (diffDays === 0) {
      return 'Today'
    }

    if (diffDays === 1) {
      return 'Tomorrow'
    }

    if (diffDays === -1) {
      return 'Yesterday'
    }
  }

  // Get user's locale for Intl.RelativeTimeFormat
  const locale = useUserPreferencesStore.getState().language
  const relativeTimeFormatter = new Intl.RelativeTimeFormat(locale, {
    numeric: 'always',
  })

  if (diffDays > 1 && diffDays <= 6) {
    return capitalizeFirst(relativeTimeFormatter.format(diffDays, 'day'))
  }

  if (diffDays === 7) {
    return capitalizeFirst(relativeTimeFormatter.format(1, 'week'))
  }

  if (diffDays === -7) {
    return capitalizeFirst(relativeTimeFormatter.format(-1, 'week'))
  }

  if (diffDays < -1 && diffDays >= -6) {
    return capitalizeFirst(relativeTimeFormatter.format(diffDays, 'day'))
  }

  return ''
}
